import { css } from 'styled-components';
import { themeGet } from 'styled-system';

import { basicTheme, createTheme } from './basicTheme';

const colors = {
  primary: '#2849f2',
  primaryHover: '#5168f9',
  secondary: basicTheme.colors.iron,
  secondaryHover: '#4eccf9',
  tertiary: '#9ea9b2',
  alterYellow: '#ebb32a',
  lightPeriwinkle: '#d4d9fd',
  header: {
    bg: basicTheme.colors.white,
    text: basicTheme.colors.textPrimary,
    searchbar: basicTheme.colors.aquaSpring //temp
  },

  sidebar: {
    delimiter: '#536ef4',
    get bg() {
      return colors.primary;
    },
    get icon() {
      return colors.lightPeriwinkle;
    },
    focusedColor: basicTheme.colors.white,
    iconHoverBackground: '#223fdd'
  },
  focused: '#00c5eb',
  multiSelectItem: {
    bg: '#baccde',
    color: basicTheme.colors.white,
    closeIcon: basicTheme.colors.textPrimary,
    hover: {
      bg: '#c8d6e6'
    }
  },
  combobox: {
    selected: basicTheme.colors.iron,
    get focusBorder() {
      return colors.primary;
    }
  }
};

const buttons = {
  primary: {
    borderRadius: 2,
    background: colors.primary,
    color: basicTheme.colors.white,
    '&:hover': {
      background: '#5168f9'
    },
    '&:active': {
      background: '#7d8ffa'
    },
    '&:disabled': {
      color: basicTheme.colors.cloudyBlueTwo,
      background: basicTheme.colors.iceBlueTwo
    }
  },
  outline: {
    borderRadius: 2,
    border: `1px solid ${colors.primary}`,
    color: colors.primary,
    '&:hover': {
      background: '#e9ecfe'
    },
    '&:active': {
      background: '#d4dbfc'
    },
    '&:disabled': {
      color: basicTheme.colors.cloudyBlueTwo,
      border: `1px solid ${basicTheme.colors.cloudyBlueTwo}`
    }
  },
  ghost: {
    color: colors.primary,
    '&:hover': {
      background: '#e9ecfe'
    },
    '&:active': {
      background: '#d4dbfc'
    },
    '&:disabled': {
      color: basicTheme.colors.cloudyBlueTwo
    }
  },
  tertiary: {
    borderRadius: 2,
    lineHeight: 1,
    background: basicTheme.colors.iceBlueTwo,
    color: basicTheme.colors.textSecondary,
    '&:hover': {
      color: colors.primary,
      background: basicTheme.colors.iceBlueTwo
    },
    '&:active': {
      background: '#dfe8f2',
      color: colors.primary
    },
    '&:disabled': {
      background: basicTheme.colors.iceBlueTwo,
      color: basicTheme.colors.cloudyBlueTwo
    }
  }
};

const borders = {
  header: `1px solid ${basicTheme.colors.iceBlueTwo}`
};

const heights = {
  stepsbar: 84,
  stepsbarTalentPool: 48,
  legacySelect: 28
};

const inputs = {
  secondary: {
    color: basicTheme.colors.monotoneGrey,
    padding: '3px 12px',
    border: `1px solid ${basicTheme.colors.paleGreyTwo}`,
    '&::placeholder': {
      fontWeight: '300',
      color: colors.tertiary
    },
    transition: 'all ease-in-out 0.15s',
    borderRadius: 2,
    '&:focus': {
      outline: 'none',
      borderColor: colors.secondary
    }
  }
};
const links = {
  subnav: css`
    color: ${basicTheme.colors.textSecondary};
    font-size: 14px;
    font-weight: ${basicTheme.fontWeights.normal};

    &:hover,
    &.active {
      font-weight: ${basicTheme.fontWeights.bold};
      color: ${basicTheme.colors.textPrimary};
    }
  `
};

const radioButtons = {
  secondary: css`
    & > .fake-radio-button {
      min-width: 100%;
      border: 1px solid;
      border-color: ${basicTheme.colors.iceBlueTwo};
      border-radius: 4px;
      color: ${basicTheme.colors.textSecondary};
    }
    & > input:not(:disabled):not(:checked) + .fake-radio-button {
      &:hover {
        background-color: ${colors.primary};
        border-color: ${colors.primary};
        color: ${basicTheme.colors.white};
      }
    }
    &:not(:last-child) > .fake-radio-button {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &:not(:first-child) > .fake-radio-button {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    &:not(:last-child) > input:not(:checked):not(:disabled) + .fake-radio-button {
      border-right: none;
    }
    &.checked + & > .fake-radio-button,
    &.disabled + & > input:not(:checked) + .fake-radio-button {
      border-left: none;
    }
    & > input:checked + .fake-radio-button {
      border-color: ${colors.primary};
      background-color: ${basicTheme.colors.lightBlue};
    }
    & > input:disabled + .fake-radio-button {
      background-color: ${basicTheme.colors.iceBlueTwo};
      border-color: ${basicTheme.colors.cloudyBlueTwo};
      color: ${basicTheme.colors.cloudyBlueTwo};
      cursor: not-allowed;
    }
  `
};

export const stepProgressBars = {
  primary: css<{ isActive: boolean }>`
    position: relative;
    background-color: ${props => (props.isActive ? colors.primary : basicTheme.colors.white)};
    border-top: 1px solid ${props => (props.isActive ? colors.primary : basicTheme.colors.iceBlueTwo)};
    border-bottom: 1px solid ${props => (props.isActive ? colors.primary : basicTheme.colors.iceBlueTwo)};
    &:hover {
      background-color: ${props => !props.isActive && !props.disabled && basicTheme.colors.lightBlue};
      &:not(:last-child) {
        &:after {
          border-left-color: ${props => !props.isActive && !props.disabled && basicTheme.colors.lightBlue};
        }
      }
    }
    &:first-child {
      border-left: 1px solid ${basicTheme.colors.iceBlueTwo};
    }
    &:last-child {
      border-right: 1px solid ${basicTheme.colors.iceBlueTwo};
    }
    &:not(:last-child) {
      &:before {
        border-left: ${props => css`
          ${props.height / 4}px solid ${props.isActive ? colors.primary : basicTheme.colors.iceBlueTwo};
        `};
      }
      &:after {
        border-left: ${props => css`
          ${props.height / 4}px solid ${props.isActive ? colors.primary : basicTheme.colors.white};
        `};
      }
    }

    .title {
      margin-bottom: 10px;
      color: ${props => (props.isActive ? themeGet('colors.white') : themeGet('colors.textPrimary'))};
      font-size: 18px;
      font-weight: ${themeGet('fontWeights.bold')};
      line-height: 1;
    }
    .description {
      color: ${props => (props.isActive ? themeGet('colors.white') : themeGet('colors.blueGreyTwo'))};
      font-size: 14px;
      font-weight: ${themeGet('fontWeights.bold')};
      line-height: 1;
    }
  `,
  secondary: css<{ isActive: boolean }>`
    position: relative;
    background-color: ${props => (props.isActive ? '#22c993' : basicTheme.colors.white)};
    border-top: 1px solid ${props => (props.isActive ? '#22c993' : '#bbefde')};
    border-bottom: 1px solid ${props => (props.isActive ? '#22c993' : '#bbefde')};
    &:hover {
      background-color: ${props => !props.isActive && !props.disabled && '#bbefde'};
      &:not(:last-child) {
        &:after {
          border-left-color: ${props => !props.isActive && !props.disabled && '#bbefde'};
        }
      }
    }
    &:first-child {
      border-left: 1px solid #bbefde;
    }
    &:last-child {
      border-right: 1px solid #bbefde;
    }
    &:not(:last-child) {
      &:before {
        border-left: ${props => css`
        ${props.height / 4}px solid ${props.isActive ? '#22c993' : '#bbefde'};
      `};
      }
      &:after {
        border-left: ${props => css`
        ${props.height / 4}px solid ${props.isActive ? '#22c993' : basicTheme.colors.white};
      `};
      }
    }

    .title {
      margin-bottom: 10px;
      color: ${props => (!props.isActive ? basicTheme.colors.textSecondary : basicTheme.colors.white)};
      font-size: 18px;
      font-weight: ${themeGet('fontWeights.normal')};
      line-height: 1;
    }
    .description {
      color: ${props => (!props.isActive ? basicTheme.colors.textSecondary : basicTheme.colors.white)};
      font-size: 14px;
      font-weight: ${themeGet('fontWeights.bold')};
      line-height: 1;
    }

    && {
      &.when-being-dragged {
        border-left: 1px solid ${props => (props.isActive ? '#22c993' : '#bbefde')};
        z-index: 10000;
        
        &:before {
          border-left: ${props => css`
          ${props.height / 4}px solid ${props.isActive ? '#22c993' : '#bbefde'} !important;
        `};
        }
        &:after {
          border-left: ${props => css`
          ${props.height / 4}px solid ${props.isActive ? '#22c993' : basicTheme.colors.white};
        `};
        }
      }
    }
  `
};

export const employerTheme = createTheme({
  colors,
  borders,
  heights,
  inputs,
  buttons,
  links,
  radioButtons,
  stepProgressBars
});
